import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

export interface NotificationMessage {
  notificationType?: string;
  buttonText?: string;
  title?: string;
  body?: string;
  cardColor?: string;
  imageSource?: string;
}

export type NotificationHandler = (message: NotificationMessage | string) => void;

let sharedSocket: Socket | null = null;
const socketListeners: Map<string, Set<NotificationHandler>> = new Map();
let connectionCount = 0;

const getOrCreateSocket = (notificationsSocketIoServer: string): Socket => {
  if (sharedSocket && sharedSocket.connected) {
    return sharedSocket;
  }

  if (sharedSocket) {
    sharedSocket.removeAllListeners();
    sharedSocket.disconnect();
  }

  sharedSocket = io(notificationsSocketIoServer, {
    auth: {
      token: `Bearer ${BFG.BF_Token}`
    },
    reconnectionAttempts: 5,
    transports: ['websocket']
  });

  sharedSocket.on('notification', (message: NotificationMessage | string) => {
    socketListeners.forEach((handlers) => {
      handlers.forEach((handler) => handler(message));
    });
  });

  sharedSocket.on('connect_error', (error) => {
    console.error('Socket connection error:', error);
  });

  return sharedSocket;
};

const registerHandler = (key: string, handler: NotificationHandler): void => {
  if (!socketListeners.has(key)) {
    socketListeners.set(key, new Set());
  }
  socketListeners.get(key)!.add(handler);
};

const unregisterHandler = (key: string, handler: NotificationHandler): void => {
  const handlers = socketListeners.get(key);
  if (handlers) {
    handlers.delete(handler);
    if (handlers.size === 0) {
      socketListeners.delete(key);
    }
  }
};

const disconnectIfUnused = (): void => {
  connectionCount--;
  if (connectionCount <= 0 && sharedSocket) {
    socketListeners.clear();
    sharedSocket.removeAllListeners();
    sharedSocket.disconnect();
    sharedSocket = null;
    connectionCount = 0;
  }
};

interface UseNotificationSocketOptions {
  notificationsSocketIoServer: string | null;
  handlerKey: string;
  onNotification: NotificationHandler;
}

/**
 * Shared socket.io hook for notifications.
 * Creates a single socket connection shared across all components.
 * Each component registers its own handler to process notifications.
 */
export const useNotificationSocket = ({
  notificationsSocketIoServer,
  handlerKey,
  onNotification
}: UseNotificationSocketOptions): void => {
  const handlerRef = useRef(onNotification);

  useEffect(() => {
    handlerRef.current = onNotification;
  }, [onNotification]);

  useEffect(() => {
    if (!notificationsSocketIoServer) {
      return;
    }

    getOrCreateSocket(notificationsSocketIoServer);
    connectionCount++;

    const stableHandler: NotificationHandler = (message) => {
      handlerRef.current(message);
    };

    registerHandler(handlerKey, stableHandler);

    return (): void => {
      unregisterHandler(handlerKey, stableHandler);
      disconnectIfUnused();
    };
  }, [notificationsSocketIoServer, handlerKey]);
};
